import { DotLegalCheckbox, DotLegalDialog, DotLegalHeader, DotLegalSelect, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import { Box, TableCell, TableRow } from "@mui/material";
import { useTranslation } from "../../../localization/useTranslation";
import { UserResponsibilityDetailRowModel, UserResponsibilityEntityType, UserResponsibilityPropertyType } from "../ReviewUserResponsibility.type";
import useReviewUserResponsibilityDetailDialog from "./ReviewUserResponsibilityDetailDialog.hooks";
import { useReviewUserResponsibilityDetailDialogStyles } from "./ReviewUserResponsibilityDetailDialog.styles";
import DotLegalTableWithControlsLocalized from "@common/components/dotLegalTableWithControlsLocalized/DotLegalTableWithControlsLocalized";

export interface IReviewUserResponsibilityDetailDialogProps {
    userId: string;
    entityType: UserResponsibilityEntityType;
    onDialogClose: () => void;
    onSaved: () => void;
}

function ReviewUserResponsibilityDetailDialog(props: IReviewUserResponsibilityDetailDialogProps) {
    const { translateString, translateUserResponsibilityEntityType } = useTranslation();
    const styles = useReviewUserResponsibilityDetailDialogStyles();
    const {
        data,
        isLoading,
        toUser,
        fromUsers,
        toUsers,
        onToUserChange,
        toggleRowSelection,
        onSelectAllRows,
        isRowSelected,
        headerCheckBoxState,
        setSearchString,
        selectedCount,
        transferSelectedResponsibilities,
        transferButtonEnabled,
        inProgress,
    } = useReviewUserResponsibilityDetailDialog(props);

    const isContract = props.entityType === UserResponsibilityEntityType.Contract;
    const isObligation = props.entityType === UserResponsibilityEntityType.Obligation;
    const isTask = props.entityType === UserResponsibilityEntityType.Task;
    const isContractDocument = props.entityType === UserResponsibilityEntityType.ContractDocument;
    const isLegalEntityDocument = props.entityType === UserResponsibilityEntityType.LegalEntityDocument;
    const isLegalEntityAudit = props.entityType === UserResponsibilityEntityType.LegalEntityAudit;
    const isSharing = props.entityType === UserResponsibilityEntityType.Sharing;
    const isRiskMitigationPlan = props.entityType === UserResponsibilityEntityType.RiskMitigationPlan;

    const getHeaders = () => {
        const baseHeaders: Array<ITableHeader<UserResponsibilityDetailRowModel>> = [
            { property: "entityName", text: translateString("name") },
            { property: "propertyTypeName", text: translateString("field") },
        ];

        if (isTask) {
            return [
                ...baseHeaders,
                { property: "deadlineName", text: translateString("deadline") },
                { property: "groupEntityName", text: translateString("groupCompany") },
            ];
        }

        if (isRiskMitigationPlan) {
            return [
                { property: "entityName", text: translateString("name") },
                { property: "propertyTypeName", text: translateString("field") },
                { property: "scenarioName", text: translateString("scenario") },
                { property: "riskEntityTypeName", text: translateString("entityType") },
                { property: "deadlineName", text: translateString("deadline") },
            ];
        }

        if (isLegalEntityDocument) {
            return [{ property: "parentEntityName", text: translateString("legalEntity") }, ...baseHeaders];
        }

        if (isLegalEntityAudit) {
            return [
                { property: "entityName", text: translateString("name") },
                { property: "parentEntityName", text: translateString("legalEntity") },
                { property: "propertyTypeName", text: translateString("field") },
            ];
        }

        if (isContract) {
            return [
                { property: "contractUserFriendlyId", text: translateString("name") },
                { property: "propertyTypeName", text: translateString("field") },
            ];
        }

        if (isContractDocument) {
            return [{ property: "contractUserFriendlyId", text: translateString("contract") }, ...baseHeaders];
        }

        if (isObligation) {
            return [{ property: "contractUserFriendlyId", text: translateString("contract") }, ...baseHeaders];
        }

        if (isSharing) {
            return [
                { property: "parentEntityName", text: translateString("processingActivity") },
                { property: "entityName", text: translateString("name") },
                { property: "propertyTypeName", text: translateString("field") },
                { property: "sender", text: translateString("sender") },
                { property: "recipient", text: translateString("recipient") },
            ];
        }

        return baseHeaders;
    };

    const getControllerRole = (isSender: boolean, propertyType: UserResponsibilityPropertyType) => {
        if (isSender) {
            if (propertyType === UserResponsibilityPropertyType.DataProcessor) return translateString("dataController");
            if (propertyType === UserResponsibilityPropertyType.DataController) return translateString("dataController");
            if (propertyType === UserResponsibilityPropertyType.Source) return translateString("dataController");
            if (propertyType === UserResponsibilityPropertyType.Disclosure) return translateString("dataController");
            if (propertyType === UserResponsibilityPropertyType.JointDataController) return translateString("jointDataController");
        } else {
            if (propertyType === UserResponsibilityPropertyType.DataProcessor) return translateString("dataProcessor");
            if (propertyType === UserResponsibilityPropertyType.DataController) return translateString("dataProcessor");
            if (propertyType === UserResponsibilityPropertyType.Source) return translateString("dataController");
            if (propertyType === UserResponsibilityPropertyType.Disclosure) return translateString("dataController");
            if (propertyType === UserResponsibilityPropertyType.JointDataController) return translateString("jointDataController");
        }

        return "";
    };

    const renderEntityWithRole = (isSender: boolean, entity: string | null, propertyType: UserResponsibilityPropertyType) => {
        if (!entity) return "";

        return (
            <div>
                <div>{entity}</div>
                <Box sx={styles.entityRole}>{getControllerRole(isSender, propertyType)}</Box>
            </div>
        );
    };

    const dialogSize = isSharing ? "lg" : "md";

    return (
        <DotLegalDialog
            header={translateUserResponsibilityEntityType(props.entityType)}
            buttonOkText={`${translateString("transfer")} ${selectedCount > 0 ? `(${selectedCount})` : ""}`}
            okButtonDisabled={!transferButtonEnabled}
            size={dialogSize}
            open
            onDialogClose={props.onDialogClose}
            onOkClick={transferSelectedResponsibilities}
            isLoading={isLoading}
            inProgress={inProgress}
        >
            <Box sx={{ display: "flex", justifyContent: "center", marginBottom: 2 }}>
                <Box sx={{ width: "70%" }}>
                    <DotLegalHeader center fontWeight={0} headerStyle={"small"}>
                        {translateString("transferUserResponsibilityInfo")}
                    </DotLegalHeader>
                    <DotLegalSelect
                        options={fromUsers}
                        onChange={() => {}}
                        placeholder={translateString("from")}
                        label={translateString("from")}
                        noOptionsLabel={""}
                        selectedItem={props.userId}
                        disabled
                    />
                    <DotLegalSelect
                        options={toUsers}
                        onChange={onToUserChange}
                        placeholder={translateString("to")}
                        label={translateString("to")}
                        noOptionsLabel={""}
                        selectedItem={toUser}
                    />
                </Box>
            </Box>
            <Box pb={2}>
                <DotLegalTableWithControlsLocalized
                    headers={getHeaders()}
                    defaultOrderBy={
                        isContract || isContractDocument || isObligation
                            ? "contractUserFriendlyId"
                            : isSharing || isLegalEntityDocument || isLegalEntityAudit
                              ? "parentEntityName"
                              : "entityName"
                    }
                    data={data}
                    rowsPerPage={20}
                    paginationLabelOf={translateString("labelOf")}
                    isLoading={isLoading}
                    clickableRows={false}
                    headerCheckBoxState={headerCheckBoxState}
                    onSelectAllChanged={onSelectAllRows}
                    onControlChange={setSearchString}
                    renderRow={(row, i) => {
                        const checkboxCell = (
                            <TableCell padding="checkbox">
                                <DotLegalCheckbox checked={isRowSelected(row)} onChange={() => toggleRowSelection(row)} />
                            </TableCell>
                        );

                        if (isLegalEntityAudit) {
                            return (
                                <TableRow key={i}>
                                    {checkboxCell}
                                    <TableCell>{row.entityName}</TableCell>
                                    <TableCell>{row.parentEntityName ?? ""}</TableCell>
                                    <TableCell>{row.propertyTypeName}</TableCell>
                                </TableRow>
                            );
                        }

                        if (isLegalEntityDocument || isContractDocument || isObligation) {
                            return (
                                <TableRow key={i}>
                                    {checkboxCell}
                                    <TableCell>{row.parentEntityName ?? ""}</TableCell>
                                    <TableCell>{row.entityName}</TableCell>
                                    <TableCell>{row.propertyTypeName}</TableCell>
                                </TableRow>
                            );
                        }

                        if (isTask) {
                            return (
                                <TableRow key={i}>
                                    {checkboxCell}
                                    <TableCell>{row.entityName}</TableCell>
                                    <TableCell>{row.propertyTypeName}</TableCell>
                                    <TableCell>{row.deadlineName ?? ""}</TableCell>
                                    <TableCell>{row.groupEntityName}</TableCell>
                                </TableRow>
                            );
                        }

                        if (isRiskMitigationPlan) {
                            return (
                                <TableRow key={i}>
                                    {checkboxCell}
                                    <TableCell>{row.entityName}</TableCell>
                                    <TableCell>{row.propertyTypeName}</TableCell>
                                    <TableCell>{row.scenarioName ?? ""}</TableCell>
                                    <TableCell>{row.riskEntityTypeName ?? ""}</TableCell>
                                    <TableCell>{row.deadlineName ?? ""}</TableCell>
                                </TableRow>
                            );
                        }

                        if (isSharing) {
                            return (
                                <TableRow key={i}>
                                    {checkboxCell}
                                    <TableCell>{row.parentEntityName}</TableCell>
                                    <TableCell>{row.entityName}</TableCell>
                                    <TableCell>{row.propertyTypeName}</TableCell>
                                    <TableCell>{row.propertyType !== null && renderEntityWithRole(true, row.sender ?? null, row.propertyType)}</TableCell>
                                    <TableCell>
                                        {row.propertyType !== null && row.recipient && row.recipient.length > 0 && (
                                            <div>
                                                <div>{row.recipient.join(", ")}</div>
                                                <Box sx={styles.entityRole}>{getControllerRole(false, row.propertyType)}</Box>
                                            </div>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        }

                        return (
                            <TableRow key={i}>
                                {checkboxCell}
                                <TableCell>{row.entityName}</TableCell>
                                <TableCell>{row.propertyTypeName}</TableCell>
                            </TableRow>
                        );
                    }}
                    getUserSpecificPageLength={() => 20}
                />
            </Box>
        </DotLegalDialog>
    );
}

export default ReviewUserResponsibilityDetailDialog;
